import type { PetNoteAttachment } from '../store/PetStore';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

interface AttachmentViewerModalProps {
  isOpen: boolean;
  attachments: PetNoteAttachment[];
  activeIndex: number;
  isDarkMode?: boolean;
  categoryLabel?: string;
  timestampLabel?: string;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onSelectIndex?: (index: number) => void;
  titleLabel?: string;
  timestampClassName: string;
}

export default function AttachmentViewerModal({
  isOpen,
  attachments,
  activeIndex,
  isDarkMode = false,
  categoryLabel = 'Health Tracker',
  timestampLabel,
  onClose,
  onNext,
  onPrevious,
  onSelectIndex,
  titleLabel = 'Health Tracker Attachment',
  timestampClassName,
}: AttachmentViewerModalProps) {
  if (!isOpen || attachments.length === 0 || activeIndex < 0 || activeIndex >= attachments.length) {
    return null;
  }

  const activeAttachment = attachments[activeIndex];
  const baseContainerClass = isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-slate-200 bg-white text-slate-900';
  const backdropClass = isDarkMode ? 'bg-slate-100/70' : 'bg-slate-900/70';
  const controlButtonClass = isDarkMode
    ? 'bg-slate-800/80 text-slate-100 hover:bg-slate-700/90'
    : 'bg-white/80 text-slate-900 hover:bg-white';
  const closeButtonClass = isDarkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-500 hover:bg-slate-100';
  const thumbnailRingBase = isDarkMode ? 'ring-slate-700' : 'ring-slate-200';
  const stripBackgroundClass = isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-slate-50';
  const imageSurfaceClass = isDarkMode ? 'bg-slate-800' : 'bg-white';

  return (
    <div
      className={`${backdropClass} fixed inset-0 z-50 flex items-center justify-center px-4 py-8 backdrop-blur-sm`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="note-attachment-viewer-title"
      onMouseDown={event => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={`${baseContainerClass} relative w-full max-w-5xl overflow-hidden rounded-xl border shadow-2xl`}
        onMouseDown={event => {
          event.stopPropagation();
        }}
      >
        <header className={`flex flex-wrap items-start justify-between gap-3 border-b px-6 py-4 ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
          <div className="flex flex-col gap-1">
            <p className={timestampClassName}>{titleLabel}</p>
            <h2 id="note-attachment-viewer-title" className="text-lg font-semibold">
              {categoryLabel}
            </h2>
            {timestampLabel ? <p className={timestampClassName}>{timestampLabel}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`${closeButtonClass} rounded-full p-2 transition`}
            aria-label="Close attachment viewer"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        <div className={`${imageSurfaceClass} relative flex items-center justify-center px-6 py-8`}>
          {attachments.length > 1 && (
            <button
              type="button"
              onClick={onPrevious}
              className={`${controlButtonClass} absolute left-8 top-1/2 -translate-y-1/2 rounded-full p-2 shadow-lg transition`}
              aria-label="Show previous attachment"
            >
              <ChevronLeft className="h-6 w-6" aria-hidden="true" />
            </button>
          )}

          <img
            src={activeAttachment.url}
            alt={`${categoryLabel} attachment ${activeIndex + 1}`}
            className="max-h-[70vh] w-full rounded-2xl object-contain shadow-2xl"
          />

          {attachments.length > 1 && (
            <button
              type="button"
              onClick={onNext}
              className={`${controlButtonClass} absolute right-8 top-1/2 -translate-y-1/2 rounded-full p-2 shadow-lg transition`}
              aria-label="Show next attachment"
            >
              <ChevronRight className="h-6 w-6" aria-hidden="true" />
            </button>
          )}
        </div>

        {attachments.length > 1 && (
          <div className={`${stripBackgroundClass} flex items-center gap-3 overflow-x-auto border-t px-6 py-4`}>
            {attachments.map((attachment, index) => {
              const isSelected = index === activeIndex;
              return (
                <button
                  key={`${attachment.storagePath ?? attachment.url}-thumb`}
                  type="button"
                  onClick={() => onSelectIndex?.(index)}
              className={`${thumbnailRingBase} ${
                isSelected ? (isDarkMode ? 'ring-2 ring-blue-400' : 'ring-2 ring-blue-500') : 'ring-1'
              } relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg transition`}
                  aria-label={`Select attachment ${index + 1}`}
                >
                  <img
                    src={attachment.url}
                    alt={`${categoryLabel} thumbnail ${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
